import { useMemo, useState, type ReactNode } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Music4, Plus, Trash2, Pencil, Check, X, GripVertical, AlertTriangle, Download,
  Lock, LockOpen, FolderPlus,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { getContrastColor } from "../calendar/DanceStyleBadge";
import { DEFAULT_STYLE_COLOR } from "@/lib/constants";
import { cn } from "@/lib/utils";

// A new style starts as the theme's "unstyled" neutral until a colour is picked.
const NEW_STYLE_COLOR: string = DEFAULT_STYLE_COLOR;

type StyleId = Id<"danceStyles">;
type OrphanRow = { slug: string; eventCount: number; venueCount: number };
type StyleRow = {
  _id: StyleId;
  name: string;
  slug: string;
  color: string;
  group: string;
  sortOrder: number;
  core: boolean;
  eventCount: number;
  venueCount: number;
};
/** Categories top to bottom, each with its styles in order. */
type Layout = { category: string; styleIds: StyleId[] }[];

// Drag ids: styles use their document id; categories and their drop areas are prefixed.
const catId = (name: string) => `cat:${name}`;
const bodyId = (name: string) => `body:${name}`;

function slugify(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function sameLayout(a: Layout, b: Layout) {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Dragging a category only ever lands on another category; dragging a style
 * lands on another style or on a category's (possibly empty) drop area.
 */
const collisionDetection: CollisionDetection = (args) => {
  const type = args.active.data.current?.type;
  const allowed = type === "category" ? ["category"] : ["style", "body"];
  return closestCorners({
    ...args,
    droppableContainers: args.droppableContainers.filter((c) =>
      allowed.includes(c.data.current?.type)
    ),
  });
};

export function DanceStyleManager() {
  const data = useQuery(api.danceStyles.listWithUsage);
  const createStyle = useMutation(api.danceStyles.create);
  const updateStyle = useMutation(api.danceStyles.update);
  const removeStyle = useMutation(api.danceStyles.remove);
  const arrange = useMutation(api.danceStyles.arrange);
  const importDefaults = useMutation(api.danceStyles.importDefaults);
  const purgeOrphan = useMutation(api.danceStyles.purgeOrphanSlug);
  const setCore = useMutation(api.danceStyles.setCore);
  const createCategory = useMutation(api.danceStyles.createCategory);
  const renameCategory = useMutation(api.danceStyles.renameCategory);
  const deleteCategory = useMutation(api.danceStyles.deleteCategory);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The layout while a drag is in progress (and until the save lands).
  const [working, setWorking] = useState<Layout | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const [newCategory, setNewCategory] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const styles = (data?.styles ?? []) as StyleRow[];
  const styleById = useMemo(() => new Map(styles.map((s) => [s._id, s])), [styles]);

  const serverLayout: Layout = useMemo(() => {
    if (!data) return [];
    return (data.categories as string[]).map((category) => ({
      category,
      styleIds: styles.filter((s) => s.group === category).map((s) => s._id),
    }));
  }, [data, styles]);

  const layout = working ?? serverLayout;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // ─── Drag and drop ──────────────────────────────────────────────────────────

  const containerOf = (id: string, current: Layout): string | undefined => {
    if (id.startsWith("body:")) return id.slice(5);
    if (id.startsWith("cat:")) return id.slice(4);
    return current.find((c) => c.styleIds.includes(id as StyleId))?.category;
  };

  const onDragStart = ({ active }: DragStartEvent) => {
    setActiveId(String(active.id));
    setWorking(serverLayout);
    setError(null);
    setNotice(null);
  };

  // Moving a style into another category happens live, so it opens a gap there.
  const onDragOver = ({ active, over }: DragOverEvent) => {
    if (!over || active.data.current?.type !== "style") return;
    setWorking((current) => {
      if (!current) return current;
      const from = containerOf(String(active.id), current);
      const to = containerOf(String(over.id), current);
      if (!from || !to || from === to) return current;

      const next = current.map((c) => ({ ...c, styleIds: [...c.styleIds] }));
      const source = next.find((c) => c.category === from)!;
      const target = next.find((c) => c.category === to)!;
      source.styleIds = source.styleIds.filter((id) => id !== active.id);
      const overIndex = target.styleIds.indexOf(over.id as StyleId);
      const at = overIndex >= 0 ? overIndex : target.styleIds.length;
      target.styleIds.splice(at, 0, active.id as StyleId);
      return next;
    });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    const current = working;
    if (!current) return;

    let next = current;
    if (over && active.data.current?.type === "category") {
      const from = current.findIndex((c) => catId(c.category) === active.id);
      const to = current.findIndex((c) => catId(c.category) === over.id);
      if (from >= 0 && to >= 0 && from !== to) next = arrayMove(current, from, to);
    } else if (over && active.data.current?.type === "style") {
      const container = containerOf(String(active.id), current);
      const target = current.find((c) => c.category === container);
      if (target) {
        const from = target.styleIds.indexOf(active.id as StyleId);
        const to = target.styleIds.indexOf(over.id as StyleId);
        if (from >= 0 && to >= 0 && from !== to) {
          next = current.map((c) =>
            c.category === container ? { ...c, styleIds: arrayMove(c.styleIds, from, to) } : c
          );
        }
      }
    }

    if (sameLayout(next, serverLayout)) {
      setWorking(null);
      return;
    }
    setWorking(next);
    void run(async () => {
      try {
        await arrange({ layout: next });
      } finally {
        setWorking(null);
      }
    });
  };

  const onDragCancel = () => {
    setActiveId(null);
    setWorking(null);
  };

  // ─── Actions ────────────────────────────────────────────────────────────────

  const handleImportDefaults = () =>
    run(async () => {
      const res = await importDefaults({});
      setNotice(
        res.inserted === 0
          ? "Nothing to import — every default style already exists."
          : `Imported ${res.inserted} style(s)${res.skipped ? `, skipped ${res.skipped} already present` : ""}.`
      );
    });

  const handleCreateCategory = () =>
    run(async () => {
      const name = (newCategory ?? "").trim();
      if (!name) return;
      await createCategory({ name });
      setNewCategory(null);
      setNotice(`Added the "${name}" category. Drag styles into it.`);
    });

  if (data === undefined) {
    return <div className="h-40 bg-secondary/30 rounded-lg animate-pulse" />;
  }
  if (data === null) {
    return <p className="text-sm text-muted-foreground">Managing dance styles needs a moderator role.</p>;
  }

  const orphanSlugs = data.orphanSlugs as OrphanRow[];
  const canManageCore = data.canManageCore as boolean;
  const hasStyles = styles.length > 0;

  const activeStyle = activeId ? styleById.get(activeId as StyleId) : undefined;
  const activeCategory = activeId?.startsWith("cat:") ? activeId.slice(4) : null;

  return (
    <section>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h2 className="font-semibold text-lg text-foreground flex items-center gap-2">
            <Music4 className="w-5 h-5" />
            Dance Styles
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            These drive the submission form, the filters, and every badge on the site.
            {hasStyles && " Drag the handles to reorder styles or move them between categories — the order here is the order everywhere."}
          </p>
          {hasStyles && (
            <p className="text-sm text-muted-foreground mt-1">
              <Lock className="inline w-3.5 h-3.5 -mt-0.5" /> Core styles can only be deleted or
              have their slug changed by an admin.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {hasStyles && (
            <Button variant="outline" size="sm" onClick={() => setNewCategory("")} disabled={busy}>
              <FolderPlus className="w-4 h-4" />
              New category
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleImportDefaults} disabled={busy}>
            <Download className="w-4 h-4" />
            Import defaults
          </Button>
        </div>
      </div>

      {!hasStyles && (
        <div className="mb-4 rounded-lg border border-border/50 bg-secondary/30 px-4 py-3">
          <p className="text-sm text-foreground font-medium">
            No styles saved yet — the site is showing its built-in list.
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Import the defaults to start managing them here. Nothing on the site changes
            until you do.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 rounded-lg border border-border/50 bg-secondary/40 px-4 py-2 text-sm text-foreground">
          {notice}
        </div>
      )}

      {newCategory !== null && (
        <div className="flex flex-wrap items-center gap-2 mb-4 rounded-lg border border-border/50 bg-card px-3 py-3">
          <Input
            autoFocus
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateCategory();
              if (e.key === "Escape") setNewCategory(null);
            }}
            placeholder="Category name, e.g. Ballroom — Smooth"
            className="flex-1 min-w-[180px]"
          />
          <Button size="sm" onClick={handleCreateCategory} disabled={busy || !newCategory.trim()}>
            <Check className="w-4 h-4" />
            Add category
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setNewCategory(null)} aria-label="Cancel">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {hasStyles && (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <SortableContext items={layout.map((c) => catId(c.category))} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {layout.map((entry) => (
                <CategorySection
                  key={entry.category}
                  name={entry.category}
                  styles={entry.styleIds.map((id) => styleById.get(id)).filter((s): s is StyleRow => !!s)}
                  busy={busy}
                  canDelete={layout.length > 1}
                  onRename={(to) =>
                    run(async () => {
                      await renameCategory({ from: entry.category, to });
                      setNotice(`Renamed "${entry.category}" to "${to.trim()}".`);
                    })
                  }
                  onDelete={() =>
                    run(async () => {
                      if (!window.confirm(`Delete the empty "${entry.category}" category?`)) return;
                      await deleteCategory({ name: entry.category });
                      setNotice(`Deleted the "${entry.category}" category.`);
                    })
                  }
                  onAddStyle={(name, color) =>
                    run(async () => {
                      await createStyle({ name: name.trim(), color, group: entry.category });
                      setNotice(`Added "${name.trim()}" to ${entry.category}.`);
                    })
                  }
                >
                  {(style) => (
                    <StyleItem
                      key={style._id}
                      style={style}
                      busy={busy}
                      canManageCore={canManageCore}
                      run={run}
                      setNotice={setNotice}
                      updateStyle={updateStyle}
                      removeStyle={removeStyle}
                      setCore={setCore}
                    />
                  )}
                </CategorySection>
              ))}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeStyle ? (
              <div className="flex items-center gap-2 bg-card rounded-lg border border-primary/50 px-3 py-2 shadow-lg">
                <GripVertical className="w-4 h-4 text-muted-foreground" />
                <StyleSwatch style={activeStyle} />
              </div>
            ) : activeCategory ? (
              <div className="rounded-lg border border-primary/50 bg-card px-3 py-2 shadow-lg font-semibold text-sm text-foreground flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-muted-foreground" />
                {activeCategory}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Slugs on content with no style behind them */}
      {orphanSlugs.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-destructive" />
            Unrecognized styles on existing content
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            These slugs are attached to events or venues but have no style here, so they
            render as nothing. Re-create the style with that exact slug to bring it back,
            or strip it from the content.
          </p>
          <div className="space-y-2">
            {orphanSlugs.map((orphan) => (
              <div
                key={orphan.slug}
                className="flex items-center justify-between bg-card rounded-lg border border-border/50 px-4 py-2"
              >
                <div>
                  <span className="font-mono text-xs text-foreground">{orphan.slug}</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    {orphan.eventCount} event{orphan.eventCount === 1 ? "" : "s"}
                    {orphan.venueCount > 0 &&
                      `, ${orphan.venueCount} venue${orphan.venueCount === 1 ? "" : "s"}`}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground hover:text-destructive"
                  disabled={busy}
                  aria-label={`Remove ${orphan.slug} from content`}
                  onClick={() =>
                    run(async () => {
                      if (
                        !window.confirm(
                          `Remove "${orphan.slug}" from ${orphan.eventCount + orphan.venueCount} record(s)? This cannot be undone.`
                        )
                      )
                        return;
                      const res = await purgeOrphan({ slug: orphan.slug });
                      setNotice(`Removed "${orphan.slug}" from ${res.detached} record(s).`);
                    })
                  }
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────────────

function StyleSwatch({ style }: { style: Pick<StyleRow, "name" | "color"> }) {
  return (
    <span
      className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium"
      style={{ backgroundColor: style.color, color: getContrastColor(style.color) }}
    >
      {style.name}
    </span>
  );
}

type SortableHandle = Pick<ReturnType<typeof useSortable>, "attributes" | "listeners" | "setActivatorNodeRef">;

/** The grip that starts a drag — the only part of a row that does. */
function DragHandle({ label, handle }: { label: string; handle: SortableHandle }) {
  return (
    <button
      type="button"
      ref={handle.setActivatorNodeRef}
      {...handle.attributes}
      {...handle.listeners}
      className="shrink-0 p-1 -m-1 rounded text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{ touchAction: "none" }}
      aria-label={label}
    >
      <GripVertical className="w-4 h-4" />
    </button>
  );
}

function CategorySection({
  name,
  styles,
  busy,
  canDelete,
  onRename,
  onDelete,
  onAddStyle,
  children,
}: {
  name: string;
  styles: StyleRow[];
  busy: boolean;
  canDelete: boolean;
  onRename: (to: string) => void;
  onDelete: () => void;
  onAddStyle: (name: string, color: string) => void;
  children: (style: StyleRow) => ReactNode;
}) {
  const sortable = useSortable({ id: catId(name), data: { type: "category" } });
  const { setNodeRef, transform, transition, isDragging } = sortable;
  const { setNodeRef: setBodyRef, isOver } = useDroppable({ id: bodyId(name), data: { type: "body" } });

  const [renaming, setRenaming] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(NEW_STYLE_COLOR);

  const submitRename = () => {
    const to = (renaming ?? "").trim();
    setRenaming(null);
    if (to && to !== name) onRename(to);
  };

  const submitAdd = () => {
    if (!newName.trim()) return;
    onAddStyle(newName, newColor);
    setNewName("");
    setNewColor(NEW_STYLE_COLOR);
    setAdding(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn("rounded-xl border border-border bg-secondary/20", isDragging && "opacity-40")}
    >
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-border/60">
        <DragHandle label={`Drag the ${name} category`} handle={sortable} />
        {renaming !== null ? (
          <>
            <Input
              autoFocus
              value={renaming}
              onChange={(e) => setRenaming(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitRename();
                if (e.key === "Escape") setRenaming(null);
              }}
              className="h-8 flex-1 min-w-[140px] text-sm"
              aria-label="Category name"
            />
            <Button size="sm" onClick={submitRename} disabled={busy} aria-label="Save category name">
              <Check className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRenaming(null)} aria-label="Cancel">
              <X className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <>
            <h3 className="font-semibold text-sm text-foreground uppercase tracking-wider">{name}</h3>
            <span className="text-xs text-muted-foreground">{styles.length}</span>
            <div className="ml-auto flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setAdding((a) => !a)}
                disabled={busy}
                aria-label={`Add a style to ${name}`}
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add style</span>
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRenaming(name)}
                disabled={busy}
                aria-label={`Rename ${name}`}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              {styles.length === 0 && canDelete && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={onDelete}
                  disabled={busy}
                  aria-label={`Delete the ${name} category`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {adding && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-border/60">
          <Input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitAdd();
              if (e.key === "Escape") setAdding(false);
            }}
            placeholder="Style name, e.g. Forró"
            className="h-9 flex-1 min-w-[160px]"
          />
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            className="h-9 w-12 rounded-md border border-border bg-card cursor-pointer"
            aria-label="New style colour"
          />
          <Button size="sm" onClick={submitAdd} disabled={busy || !newName.trim()}>
            <Plus className="w-4 h-4" />
            Add
          </Button>
        </div>
      )}

      <SortableContext items={styles.map((s) => s._id)} strategy={verticalListSortingStrategy}>
        <div
          ref={setBodyRef}
          className={cn("p-2 space-y-1.5 min-h-[44px] rounded-b-xl transition-colors", isOver && "bg-primary/5")}
        >
          {styles.length === 0 && (
            <p className="text-xs text-muted-foreground px-2 py-2">Empty — drag styles here, or add one.</p>
          )}
          {styles.map((style) => children(style))}
        </div>
      </SortableContext>
    </div>
  );
}

function StyleItem({
  style,
  busy,
  canManageCore,
  run,
  setNotice,
  updateStyle,
  removeStyle,
  setCore,
}: {
  style: StyleRow;
  busy: boolean;
  canManageCore: boolean;
  run: (fn: () => Promise<void>) => Promise<void>;
  setNotice: (notice: string | null) => void;
  updateStyle: (args: { styleId: StyleId; name: string; slug: string; color: string }) => Promise<{ migrated: number }>;
  removeStyle: (args: { styleId: StyleId; detachFromContent?: boolean }) => Promise<{ detached: number }>;
  setCore: (args: { styleId: StyleId; core: boolean }) => Promise<null>;
}) {
  const sortable = useSortable({ id: style._id, data: { type: "style" } });
  const { setNodeRef, transform, transition, isDragging } = sortable;

  const [draft, setDraft] = useState<{ name: string; slug: string; color: string } | null>(null);
  // A core style is locked for moderators; admins can change anything.
  const locked = style.core && !canManageCore;
  const usage = style.eventCount + style.venueCount;

  const saveEdit = () =>
    run(async () => {
      if (!draft) return;
      const slugChanged = slugify(draft.slug) !== style.slug;
      if (slugChanged && usage > 0) {
        const ok = window.confirm(
          `Changing the slug from "${style.slug}" to "${slugify(draft.slug)}" will ` +
            `rewrite ${usage} event(s)/venue(s) that reference it. Continue?`
        );
        if (!ok) return;
      }
      const res = await updateStyle({ styleId: style._id, ...draft });
      setDraft(null);
      setNotice(
        res.migrated > 0 ? `Saved. Updated the slug on ${res.migrated} event(s)/venue(s).` : "Saved."
      );
    });

  const handleDelete = () =>
    run(async () => {
      const message =
        usage > 0
          ? `Delete "${style.name}"? It is used by ${style.eventCount} event(s) and ` +
            `${style.venueCount} venue(s) — the style will be removed from all of them. ` +
            `This cannot be undone.`
          : `Delete "${style.name}"? This cannot be undone.`;
      if (!window.confirm(message)) return;
      const res = await removeStyle({ styleId: style._id, detachFromContent: usage > 0 });
      setNotice(
        res.detached > 0
          ? `Deleted "${style.name}" and removed it from ${res.detached} record(s).`
          : `Deleted "${style.name}".`
      );
    });

  const toggleCore = () =>
    run(async () => {
      await setCore({ styleId: style._id, core: !style.core });
      setNotice(
        style.core
          ? `"${style.name}" is no longer core — moderators can rename or delete it.`
          : `"${style.name}" is now core — only admins can change its slug or delete it.`
      );
    });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex flex-wrap items-center gap-2 bg-card rounded-lg border border-border/50 px-3 py-2",
        isDragging && "opacity-40"
      )}
    >
      <DragHandle label={`Drag ${style.name}`} handle={sortable} />

      {draft ? (
        <>
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="h-9 flex-1 min-w-[140px]"
            aria-label="Style name"
          />
          <Input
            value={draft.slug}
            onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
            disabled={locked}
            title={locked ? "Core style — only an admin can change the slug" : undefined}
            className="h-9 flex-1 min-w-[120px] font-mono text-xs"
            aria-label="Slug"
          />
          <input
            type="color"
            value={draft.color}
            onChange={(e) => setDraft({ ...draft, color: e.target.value })}
            className="h-9 w-12 rounded-md border border-border bg-card cursor-pointer"
            aria-label="Style colour"
          />
          <Button size="sm" onClick={saveEdit} disabled={busy}>
            <Check className="w-4 h-4" />
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDraft(null)} disabled={busy} aria-label="Cancel">
            <X className="w-4 h-4" />
          </Button>
        </>
      ) : (
        <>
          <StyleSwatch style={style} />
          {style.core && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-medium text-primary"
              title="Core style — only admins can change its slug or delete it"
            >
              <Lock className="w-3 h-3" />
              Core
            </span>
          )}
          <span className="font-mono text-xs text-muted-foreground hidden sm:inline">{style.slug}</span>
          <span className="text-xs text-muted-foreground">
            {style.eventCount} event{style.eventCount === 1 ? "" : "s"}
            {style.venueCount > 0 && `, ${style.venueCount} venue${style.venueCount === 1 ? "" : "s"}`}
          </span>

          <div className="ml-auto flex items-center gap-1">
            {canManageCore && (
              <Button
                size="sm"
                variant="ghost"
                className={style.core ? "text-primary" : "text-muted-foreground"}
                onClick={toggleCore}
                disabled={busy}
                aria-label={style.core ? `Unlock ${style.name}` : `Mark ${style.name} as core`}
                title={style.core ? "Core — click to unlock" : "Mark as core"}
              >
                {style.core ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDraft({ name: style.name, slug: style.slug, color: style.color })}
              disabled={busy}
              aria-label={`Edit ${style.name}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-destructive"
              onClick={handleDelete}
              disabled={busy || locked}
              title={locked ? "Core style — only an admin can delete it" : undefined}
              aria-label={`Delete ${style.name}${usage > 0 ? ` (in use by ${usage})` : ""}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
